import { useState, useEffect, useRef } from "react"
import { Search, FileText, FolderOpen, ChevronRight, ChevronDown, Library, Check } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import { FileTypeIcon } from "@/components/ui/file-type-icon"
import { useLibraryStore } from "@/stores/library-store"
import { useBidsStore } from "@/stores/bids-store"
import { api } from "@/lib/api"
import type { Document } from "@/lib/schemas"

export type EvidenceScope = "auto" | "fact_bank" | "documents"

/** Mirrors the backend `selectedDocumentIds` `.max(100)` on the turn schema. */
const MAX_SELECTED_DOCUMENTS = 100
const CAP_MESSAGE = "Selection is limited to 100 documents"

interface EvidenceSelectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedIds: string[]
  /**
   * `names` maps every emitted id to its display name (badge labelling).
   * `scope` is the scope in force at Apply — the explicit choice wins, so a
   * non-`documents` scope always emits an empty selection.
   */
  onSelectionChange: (
    ids: string[],
    names: Record<string, string>,
    scope: EvidenceScope
  ) => void
  /** Names for previously selected ids, so unloaded docs survive a reopen. */
  selectedNames?: Record<string, string>
  mode: "plan" | "chat" | "agent"
  /** When set, the Tender Documents tab lists only this bid. */
  currentBidId?: string
  /**
   * Answer-time scope (Layer 1). When provided, the dialog shows a top-level
   * scope mode (Let AI pick · Fact Bank only · Choose documents) and the
   * document multiselect is only active for "documents". When omitted, the
   * dialog behaves as a plain document picker (plan mode / legacy callers).
   *
   * This seeds a LOCAL draft each time the dialog opens — the caller's state is
   * only updated by Apply (third arg of `onSelectionChange`), so Cancel and
   * dismiss discard the change like every other edit in this dialog.
   */
  scope?: EvidenceScope
}

/**
 * Painted checkbox for rows where the row itself is the control. Radix's
 * Checkbox renders a <button>, which cannot legally nest inside the row button.
 */
function CheckboxMark({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded border border-[hsl(var(--brand))]",
        checked && "bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]"
      )}
    >
      {checked && <Check className="h-4 w-4" />}
    </span>
  )
}

interface SelectedDocument {
  id: string
  name: string
  /** `unknown` = an id we can't resolve yet (docs not loaded) — kept, not dropped. */
  type: "library" | "tender" | "unknown"
  folderId?: string
  bidId?: string
}

export function EvidenceSelectDialog({
  open,
  onOpenChange,
  selectedIds,
  onSelectionChange,
  selectedNames,
  mode,
  currentBidId,
  scope,
}: EvidenceSelectDialogProps) {
  const showScopeControl = scope !== undefined
  const isPlanMode = mode === "plan"
  const includeTenderDocs = mode === "chat" || mode === "agent"
  const usesEvidenceTerminology = mode !== "chat"

  const {
    documents: libraryDocuments,
    folders,
    fetchDocuments: fetchLibraryDocs,
    fetchFolders,
    getChildFolders,
    getRootFolders,
  } = useLibraryStore()
  const { bids, fetchBids } = useBidsStore()

  const [searchQuery, setSearchQuery] = useState("")
  // Draft scope: re-seeded from the prop on every open, committed only by Apply.
  const [draftScope, setDraftScope] = useState<EvidenceScope>(scope ?? "documents")
  const [selectedDocs, setSelectedDocs] = useState<SelectedDocument[]>([])
  const [selectAllLibrary, setSelectAllLibrary] = useState(false)
  const [activeTab, setActiveTab] = useState<"library" | "tender">("library")
  const [capNoticeVisible, setCapNoticeVisible] = useState(false)

  const effectiveScope: EvidenceScope = showScopeControl ? draftScope : "documents"
  const showDocPicker = effectiveScope === "documents"

  // Only the current bid's documents are pickable in a bid-scoped surface —
  // the backend fence rejects cross-bid pins.
  const scopedBids = currentBidId ? bids.filter((bid) => bid.id === currentBidId) : bids

  // Folder expansion state
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())

  // Bid expansion state and documents
  const [expandedBids, setExpandedBids] = useState<Set<string>>(new Set())
  const [bidDocuments, setBidDocuments] = useState<Record<string, Document[]>>({})
  const [loadingBidDocs, setLoadingBidDocs] = useState<Set<string>>(new Set())

  // Fetch data when dialog opens
  useEffect(() => {
    if (open) {
      void fetchLibraryDocs()
      void fetchFolders()
      if (includeTenderDocs) {
        void fetchBids()
      }
    }
  }, [open, fetchLibraryDocs, fetchFolders, fetchBids, includeTenderDocs])

  // Auto-load all bid documents when dialog opens and bids are available
  useEffect(() => {
    if (open && includeTenderDocs && scopedBids.length > 0) {
      scopedBids.forEach((bid) => {
        if (!bidDocuments[bid.id] && !loadingBidDocs.has(bid.id)) {
          void loadBidDocuments(bid.id)
        }
      })
    }
  }, [open, bids, currentBidId, includeTenderDocs])

  const findLoadedDoc = (id: string): SelectedDocument | null => {
    const libraryDoc = libraryDocuments.find((d) => d.id === id)
    if (libraryDoc) {
      const doc: SelectedDocument = { id: libraryDoc.id, name: libraryDoc.name, type: "library" }
      if (libraryDoc.folderId) {
        doc.folderId = libraryDoc.folderId
      }
      return doc
    }
    for (const [bidId, docs] of Object.entries(bidDocuments)) {
      const bidDoc = docs.find((d) => d.id === id)
      if (bidDoc) {
        return { id: bidDoc.id, name: bidDoc.name, type: "tender", bidId }
      }
    }
    return null
  }

  // Initialize selected docs on the open false→true transition ONLY. Re-running
  // when library/bid documents arrive would wipe in-progress checkbox state.
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSelectedDocs((previous) => {
        const previousById = new Map(previous.map((doc) => [doc.id, doc]))
        return selectedIds.map((id) => (
          findLoadedDoc(id)
          ?? previousById.get(id)
          ?? { id, name: selectedNames?.[id] ?? "Document", type: "unknown" as const }
        ))
      })
      setSelectAllLibrary(false)
      setCapNoticeVisible(false)
      setSearchQuery("")
      setDraftScope(scope ?? "documents")
    }
    wasOpenRef.current = open
  }, [open, selectedIds, scope])

  // Late-loading documents refine unresolved entries in place — never add or
  // remove selections (that would fight the user's in-progress edits).
  useEffect(() => {
    if (!open) return
    setSelectedDocs((previous) => {
      let changed = false
      const next = previous.map((doc) => {
        if (doc.type !== "unknown") return doc
        const resolved = findLoadedDoc(doc.id)
        if (!resolved) return doc
        changed = true
        return resolved
      })
      return changed ? next : previous
    })
  }, [open, libraryDocuments, bidDocuments])

  // Filter documents/folders/bids by search. A container (folder or bid) stays
  // visible when its own name matches OR when it holds a matching document —
  // otherwise a filename search could never reach a filed document.
  const searchTerm = searchQuery.trim().toLowerCase()
  const isSearching = searchTerm.length > 0
  const matchesSearch = (value: string) => value.toLowerCase().includes(searchTerm)

  const filteredDocs = libraryDocuments.filter((doc) => matchesSearch(doc.name))

  const folderNameMatches = (folderId: string) => {
    const folder = folders.find((f) => f.id === folderId)
    return !!folder && matchesSearch(folder.name)
  }

  const folderMatchesSearch = (folderId: string): boolean => {
    if (folderNameMatches(folderId)) return true
    if (libraryDocuments.some((doc) => doc.folderId === folderId && matchesSearch(doc.name))) {
      return true
    }
    return getChildFolders(folderId).some((child) => folderMatchesSearch(child.id))
  }

  const bidNameMatches = (bidId: string) => {
    const bid = scopedBids.find((b) => b.id === bidId)
    return !!bid && matchesSearch(bid.title)
  }

  const filteredBids = scopedBids.filter(
    (bid) =>
      !isSearching ||
      bidNameMatches(bid.id) ||
      (bidDocuments[bid.id] ?? []).some((doc) => matchesSearch(doc.name))
  )

  // Selection helpers
  const isDocSelected = (docId: string) => selectedDocs.some((d) => d.id === docId)

  // Cap accounting: "select all library" stands in for every library doc.
  const nonLibrarySelectedCount = selectedDocs.filter((d) => d.type !== "library").length
  const selectionCount = selectAllLibrary
    ? libraryDocuments.length + nonLibrarySelectedCount
    : selectedDocs.length
  const remainingCapacity = Math.max(0, MAX_SELECTED_DOCUMENTS - selectionCount)
  const selectAllLibraryExceedsCap =
    libraryDocuments.length + nonLibrarySelectedCount > MAX_SELECTED_DOCUMENTS
  const showCapMessage = capNoticeVisible || selectAllLibraryExceedsCap

  const toggleDocument = (
    doc: { id: string; name: string; folderId?: string | null },
    type: "library" | "tender",
    bidId?: string
  ) => {
    if (type === "library" && selectAllLibrary) {
      setSelectAllLibrary(false)
    }

    if (isDocSelected(doc.id)) {
      setCapNoticeVisible(false)
      setSelectedDocs(selectedDocs.filter((d) => d.id !== doc.id))
    } else if (remainingCapacity === 0) {
      setCapNoticeVisible(true)
    } else {
      const newDoc: SelectedDocument = {
        id: doc.id,
        name: doc.name,
        type,
      }
      if (type === "library" && doc.folderId) {
        newDoc.folderId = doc.folderId
      }
      if (type === "tender" && bidId) {
        newDoc.bidId = bidId
      }
      setSelectedDocs([...selectedDocs, newDoc])
    }
  }

  // Folder helpers
  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId)
    } else {
      newExpanded.add(folderId)
    }
    setExpandedFolders(newExpanded)
  }

  const isFolderExpanded = (folderId: string) => expandedFolders.has(folderId)

  // When the folder itself is the match, every document inside it is a result.
  const getDocsInFolder = (folderId: string | null) => {
    if (folderId !== null && isSearching && folderNameMatches(folderId)) {
      return libraryDocuments.filter((doc) => doc.folderId === folderId)
    }
    return filteredDocs.filter((doc) => doc.folderId === folderId)
  }

  const getDocsInBid = (bidId: string) => {
    const docs = bidDocuments[bidId] ?? []
    if (!isSearching || bidNameMatches(bidId)) return docs
    return docs.filter((doc) => matchesSearch(doc.name))
  }

  const getDocCountInFolder = (folderId: string): number => {
    const directDocs = libraryDocuments.filter((doc) => doc.folderId === folderId).length
    const childFolders = getChildFolders(folderId)
    const childDocs = childFolders.reduce((sum, folder) => sum + getDocCountInFolder(folder.id), 0)
    return directDocs + childDocs
  }

  const selectAllInFolder = (folderId: string, selected: boolean) => {
    const docsInFolder = libraryDocuments.filter((doc) => doc.folderId === folderId)
    if (selected) {
      const candidates: SelectedDocument[] = docsInFolder
        .filter((doc) => !isDocSelected(doc.id))
        .map((doc) => ({
          id: doc.id,
          name: doc.name,
          type: "library" as const,
          folderId,
        }))
      const newDocs = candidates.slice(0, remainingCapacity)
      if (newDocs.length < candidates.length) {
        setCapNoticeVisible(true)
      }
      setSelectedDocs([...selectedDocs, ...newDocs])
    } else {
      setCapNoticeVisible(false)
      setSelectedDocs(selectedDocs.filter((d) => d.folderId !== folderId))
    }
    setSelectAllLibrary(false)
  }

  const isFolderFullySelected = (folderId: string) => {
    const docsInFolder = libraryDocuments.filter((doc) => doc.folderId === folderId)
    return docsInFolder.length > 0 && docsInFolder.every((doc) => isDocSelected(doc.id))
  }

  const isFolderPartiallySelected = (folderId: string) => {
    const docsInFolder = libraryDocuments.filter((doc) => doc.folderId === folderId)
    const selectedCount = docsInFolder.filter((doc) => isDocSelected(doc.id)).length
    return selectedCount > 0 && selectedCount < docsInFolder.length
  }

  // Bid helpers
  const toggleBid = (bidId: string) => {
    const newExpanded = new Set(expandedBids)
    if (newExpanded.has(bidId)) {
      newExpanded.delete(bidId)
    } else {
      newExpanded.add(bidId)
    }
    setExpandedBids(newExpanded)
  }

  const loadBidDocuments = async (bidId: string) => {
    setLoadingBidDocs((prev) => new Set([...prev, bidId]))
    try {
      const docs = await api.get<Document[]>(`/bids/${bidId}/documents`)
      setBidDocuments((prev) => ({ ...prev, [bidId]: docs }))
    } catch (error) {
      console.error("Failed to load bid documents:", error)
      setBidDocuments((prev) => ({ ...prev, [bidId]: [] }))
    } finally {
      setLoadingBidDocs((prev) => {
        const next = new Set(prev)
        next.delete(bidId)
        return next
      })
    }
  }

  const isBidFullySelected = (bidId: string) => {
    const docs = bidDocuments[bidId] ?? []
    return docs.length > 0 && docs.every((doc) => isDocSelected(doc.id))
  }

  const isBidPartiallySelected = (bidId: string) => {
    const docs = bidDocuments[bidId] ?? []
    const selectedCount = docs.filter((doc) => isDocSelected(doc.id)).length
    return selectedCount > 0 && selectedCount < docs.length
  }

  /** Bid documents the user can actually pick — scoped to the visible bids. */
  const selectableBidDocumentEntries = (): Array<[string, Document[]]> =>
    Object.entries(bidDocuments).filter(([bidId]) => scopedBids.some((bid) => bid.id === bidId))

  const selectAllInBid = (bidId: string, selected: boolean) => {
    const docs = bidDocuments[bidId] ?? []
    if (selected) {
      const candidates: SelectedDocument[] = docs
        .filter((doc) => !isDocSelected(doc.id))
        .map((doc) => ({
          id: doc.id,
          name: doc.name,
          type: "tender" as const,
          bidId,
        }))
      const newDocs = candidates.slice(0, remainingCapacity)
      if (newDocs.length < candidates.length) {
        setCapNoticeVisible(true)
      }
      setSelectedDocs([...selectedDocs, ...newDocs])
    } else {
      setCapNoticeVisible(false)
      setSelectedDocs(selectedDocs.filter((d) => d.bidId !== bidId))
    }
  }

  // Select all handlers
  const handleSelectAllLibrary = (checked: boolean) => {
    if (checked && selectAllLibraryExceedsCap) {
      setCapNoticeVisible(true)
      return
    }
    setSelectAllLibrary(checked)
    if (checked) {
      setSelectedDocs(selectedDocs.filter((d) => d.type !== "library"))
    } else {
      setCapNoticeVisible(false)
    }
  }

  const handleSelectAllTender = (checked: boolean) => {
    if (checked) {
      const allTenderDocs: SelectedDocument[] = []
      selectableBidDocumentEntries().forEach(([bidId, docs]) => {
        docs.forEach((doc) => {
          if (!isDocSelected(doc.id)) {
            allTenderDocs.push({
              id: doc.id,
              name: doc.name,
              type: "tender",
              bidId,
            })
          }
        })
      })
      const newDocs = allTenderDocs.slice(0, remainingCapacity)
      if (newDocs.length < allTenderDocs.length) {
        setCapNoticeVisible(true)
      }
      setSelectedDocs([...selectedDocs, ...newDocs])
    } else {
      setCapNoticeVisible(false)
      setSelectedDocs(selectedDocs.filter((d) => d.type !== "tender"))
    }
  }

  const areAllTenderDocsSelected = () => {
    const loadedTenderDocs = selectableBidDocumentEntries().flatMap(([, docs]) => docs)
    return loadedTenderDocs.length > 0 && loadedTenderDocs.every((doc) => isDocSelected(doc.id))
  }

  const areSomeTenderDocsSelected = () => {
    const loadedTenderDocs = selectableBidDocumentEntries().flatMap(([, docs]) => docs)
    const selectedCount = loadedTenderDocs.filter((doc) => isDocSelected(doc.id)).length
    return selectedCount > 0 && selectedCount < loadedTenderDocs.length
  }

  const handleApply = () => {
    // An explicit "Fact Bank only" / "Let AI pick" beats whatever is ticked —
    // the picks don't apply under those scopes, so they must not be emitted.
    if (effectiveScope !== "documents") {
      onSelectionChange([], {}, effectiveScope)
      onOpenChange(false)
      return
    }

    const applied: SelectedDocument[] = []

    if (selectAllLibrary) {
      libraryDocuments.forEach((doc) => {
        applied.push({ id: doc.id, name: doc.name, type: "library" })
      })
    } else {
      applied.push(...selectedDocs.filter((d) => d.type === "library"))
    }

    // Tender picks only count when that tab is enabled; unresolved ids are
    // always carried through so a reopen can't silently drop a selection.
    applied.push(
      ...selectedDocs.filter((d) => (
        d.type === "unknown" || (includeTenderDocs && d.type === "tender")
      ))
    )

    const ids: string[] = []
    const names: Record<string, string> = {}
    for (const doc of applied) {
      if (names[doc.id] !== undefined) continue
      if (ids.length >= MAX_SELECTED_DOCUMENTS) break
      ids.push(doc.id)
      names[doc.id] = doc.name
    }

    onSelectionChange(ids, names, "documents")
    onOpenChange(false)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  // Get unfiled documents (no folder)
  const unfiledDocs = filteredDocs.filter((doc) => !doc.folderId)

  // Get selection summary text
  const getSelectionSummary = () => {
    // Under a non-document scope the ticked docs never apply — claiming a count
    // there would promise something Apply won't honour.
    if (effectiveScope !== "documents") {
      return effectiveScope === "fact_bank"
        ? "Answering from the Fact Bank only"
        : "The AI will choose the documents"
    }
    const libraryCount = selectAllLibrary
      ? libraryDocuments.length
      : selectedDocs.filter((d) => d.type === "library").length
    const tenderCount = selectedDocs.filter((d) => d.type === "tender").length
    const unknownCount = selectedDocs.filter((d) => d.type === "unknown").length
    const parts: string[] = []
    if (libraryCount > 0) {
      parts.push(`${libraryCount} library doc${libraryCount !== 1 ? "s" : ""}`)
    }
    if (includeTenderDocs && tenderCount > 0) {
      parts.push(`${tenderCount} tender doc${tenderCount !== 1 ? "s" : ""}`)
    }
    if (unknownCount > 0) {
      parts.push(`${unknownCount} other doc${unknownCount !== 1 ? "s" : ""}`)
    }
    return parts.length > 0 ? parts.join(", ") : "No documents selected"
  }

  // Render folder tree recursively
  const renderFolderTree = (parentId: string | null, depth: number = 0) => {
    const childFolders = parentId === null ? getRootFolders() : getChildFolders(parentId)
    const folderList = isSearching
      ? childFolders.filter((f) => folderMatchesSearch(f.id))
      : childFolders

    return folderList.map((folder) => {
      // A search result is useless collapsed — surviving folders auto-expand.
      const isExpanded = isSearching || isFolderExpanded(folder.id)
      const docCount = getDocCountInFolder(folder.id)
      const docsInFolder = getDocsInFolder(folder.id)
      const hasChildren = getChildFolders(folder.id).length > 0 || docsInFolder.length > 0
      const isFullySelected = isFolderFullySelected(folder.id)
      const isPartiallySelected = isFolderPartiallySelected(folder.id)

      return (
        <div key={folder.id}>
          <div
            className={cn(
              "flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer overflow-hidden",
              depth > 0 && "ml-4"
            )}
          >
            <button
              className="p-0.5 hover:bg-muted rounded flex-shrink-0"
              onClick={() => toggleFolder(folder.id)}
            >
              {hasChildren ? (
                isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )
              ) : (
                <div className="w-4" />
              )}
            </button>
            <Checkbox
              checked={isFullySelected}
              ref={(el) => {
                if (el && isPartiallySelected) {
                  el.dataset["state"] = "indeterminate"
                }
              }}
              onCheckedChange={(checked) => selectAllInFolder(folder.id, checked === true)}
              className="pointer-events-auto flex-shrink-0"
            />
            <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-sm flex-1 truncate min-w-0">{folder.name}</span>
            <span className="text-meta flex-shrink-0">({docCount})</span>
          </div>

          {isExpanded && (
            <div className="ml-4 overflow-hidden">
              {renderFolderTree(folder.id, depth + 1)}

              {docsInFolder.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  role="checkbox"
                  aria-checked={isDocSelected(doc.id)}
                  className={cn(
                    "w-full min-w-0 flex items-center gap-2 px-2 py-1.5 ml-4 rounded-md text-left transition-colors overflow-hidden",
                    isDocSelected(doc.id) ? "bg-primary/10 text-primary" : "hover:bg-accent"
                  )}
                  onClick={() =>
                    toggleDocument({ id: doc.id, name: doc.name, folderId: doc.folderId ?? null }, "library")
                  }
                >
                  <div className="w-4 flex-shrink-0" />
                  <CheckboxMark checked={isDocSelected(doc.id)} />
                  <FileTypeIcon mimeType={doc.mimeType} fileName={doc.name} size={20} className="flex-shrink-0" />
                  <span className="text-sm truncate min-w-0">{doc.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )
    })
  }

  // Library content component
  const renderLibraryContent = () => (
    <>
      <div className="flex items-center gap-2 px-2 py-2 border-b border-border mb-2">
        <Checkbox
          checked={selectAllLibrary}
          disabled={selectAllLibraryExceedsCap}
          onCheckedChange={handleSelectAllLibrary}
        />
        <Library className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Select All Library Documents</span>
        {selectAllLibraryExceedsCap && (
          <span className="text-meta">{CAP_MESSAGE}</span>
        )}
      </div>

      <ScrollArea className="flex-1 min-h-0 w-full">
        {renderFolderTree(null)}

        {unfiledDocs.length > 0 && (
          <div className="mt-2">
            {unfiledDocs.map((doc) => (
              <button
                key={doc.id}
                type="button"
                role="checkbox"
                aria-checked={isDocSelected(doc.id) || selectAllLibrary}
                className={cn(
                  "w-full min-w-0 flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors overflow-hidden",
                  isDocSelected(doc.id) || selectAllLibrary
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-accent"
                )}
                onClick={() =>
                  toggleDocument({ id: doc.id, name: doc.name, folderId: doc.folderId ?? null }, "library")
                }
              >
                <div className="w-4 flex-shrink-0" />
                <CheckboxMark checked={isDocSelected(doc.id) || selectAllLibrary} />
                <FileTypeIcon mimeType={doc.mimeType} fileName={doc.name} size={20} className="flex-shrink-0" />
                <span className="text-sm truncate min-w-0">{doc.name}</span>
              </button>
            ))}
          </div>
        )}

        {filteredDocs.length === 0 && folders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <FileText className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No documents in library</p>
          </div>
        )}
      </ScrollArea>
    </>
  )

  // Tender documents content component (chat mode only)
  const renderTenderContent = () => (
    <>
      <div className="flex items-center gap-2 px-2 py-2 border-b border-border mb-2">
        <Checkbox
          checked={areAllTenderDocsSelected()}
          ref={(el) => {
            if (el && areSomeTenderDocsSelected()) {
              el.dataset["state"] = "indeterminate"
            }
          }}
          onCheckedChange={handleSelectAllTender}
        />
        <FolderOpen className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Select All Tender Documents</span>
        {loadingBidDocs.size > 0 && (
          <span className="text-meta">(loading...)</span>
        )}
      </div>

      <ScrollArea className="flex-1 min-h-0 w-full">
        {filteredBids.map((bid) => {
          const isExpanded = isSearching || expandedBids.has(bid.id)
          const docs = bidDocuments[bid.id] ?? []
          const visibleDocs = getDocsInBid(bid.id)
          const isLoading = loadingBidDocs.has(bid.id)
          const isFullySelected = isBidFullySelected(bid.id)
          const isPartiallySelected = isBidPartiallySelected(bid.id)

          return (
            <div key={bid.id}>
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer overflow-hidden">
                <button
                  className="p-0.5 hover:bg-muted rounded flex-shrink-0"
                  onClick={() => toggleBid(bid.id)}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
                <Checkbox
                  checked={isFullySelected}
                  disabled={isLoading}
                  ref={(el) => {
                    if (el && isPartiallySelected) {
                      el.dataset["state"] = "indeterminate"
                    }
                  }}
                  onCheckedChange={(checked) => selectAllInBid(bid.id, checked === true)}
                  className="pointer-events-auto flex-shrink-0"
                />
                <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm flex-1 truncate min-w-0">{bid.title}</span>
                {isLoading ? (
                  <span className="text-meta flex-shrink-0">Loading...</span>
                ) : (
                  <span className="text-meta flex-shrink-0">({docs.length})</span>
                )}
              </div>

              {isExpanded && (
                <div className="ml-4 overflow-hidden">
                  {visibleDocs.length === 0 ? (
                    <div className="px-6 py-2 text-sm text-muted-foreground">
                      {docs.length === 0
                        ? "No documents uploaded for this bid"
                        : "No matching documents"}
                    </div>
                  ) : (
                    visibleDocs.map((doc) => (
                      <button
                        key={doc.id}
                        type="button"
                        role="checkbox"
                        aria-checked={isDocSelected(doc.id)}
                        className={cn(
                          "w-full min-w-0 flex items-center gap-2 px-2 py-1.5 ml-4 rounded-md text-left transition-colors overflow-hidden",
                          isDocSelected(doc.id) ? "bg-primary/10 text-primary" : "hover:bg-accent"
                        )}
                        onClick={() => toggleDocument({ id: doc.id, name: doc.name }, "tender", bid.id)}
                      >
                        <div className="w-4 flex-shrink-0" />
                        <CheckboxMark checked={isDocSelected(doc.id)} />
                        <FileTypeIcon mimeType={doc.mimeType} fileName={doc.name} size={20} className="flex-shrink-0" />
                        <span className="text-sm truncate min-w-0">{doc.name}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )
        })}

        {scopedBids.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <FolderOpen className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No bids found</p>
          </div>
        )}
      </ScrollArea>
    </>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{usesEvidenceTerminology ? "Select Evidence" : "Select Context"}</DialogTitle>
          <DialogDescription>
            {usesEvidenceTerminology
              ? "Choose which evidence the AI should draw on for this response."
              : "Choose which documents the AI should draw on for this conversation."}
          </DialogDescription>
        </DialogHeader>

        {showScopeControl && (
          <div className="flex-shrink-0 mb-3">
            <div className="inline-flex items-center gap-1 rounded-lg bg-muted p-1 h-9">
              {([
                { key: "auto", label: "Let AI pick" },
                { key: "fact_bank", label: "Fact Bank only" },
                { key: "documents", label: "Choose documents" },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setDraftScope(opt.key)}
                  className={cn(
                    "px-3 h-7 rounded-md text-sm transition-colors",
                    effectiveScope === opt.key
                      ? "bg-card shadow-sm font-medium text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {showScopeControl && effectiveScope !== "documents" && (
          <div className="flex-1 min-h-0 flex items-center justify-center text-center px-6">
            <p className="text-sm text-muted-foreground max-w-sm">
              {effectiveScope === "fact_bank"
                ? "The AI will answer only from your Fact Bank — the company documents you've marked as authoritative (insurances, certifications, policies)."
                : "The AI will choose the most relevant documents automatically."}
            </p>
          </div>
        )}

        {showDocPicker && (isPlanMode ? (
          // Plan mode: Library only (no tabs)
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            {renderLibraryContent()}
          </div>
        ) : (
          // Chat mode: Both Library and Tender tabs
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as typeof activeTab)}
            className="flex-1 min-h-0 flex flex-col"
          >
            <TabsList className="w-full">
              <TabsTrigger value="library" className="flex-1">
                Library
              </TabsTrigger>
              <TabsTrigger value="tender" className="flex-1">
                Tender Documents
              </TabsTrigger>
            </TabsList>

            <div className="mt-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={
                    activeTab === "library"
                      ? "Search documents..."
                      : "Search bids and documents..."
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <TabsContent value="library" className="mt-4 flex-1 min-h-0 flex flex-col">
              {renderLibraryContent()}
            </TabsContent>

            <TabsContent value="tender" className="mt-4 flex-1 min-h-0 flex flex-col">
              {renderTenderContent()}
            </TabsContent>
          </Tabs>
        ))}

        <DialogFooter className="flex-shrink-0 flex items-center justify-between sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {getSelectionSummary()}
            {showDocPicker && showCapMessage && (
              <span className="ml-2 text-xs">{CAP_MESSAGE}</span>
            )}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleApply}>Apply</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
